import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { updateWarehouseStatus } from '@/services/procurementWarehouseStatusService';
import type { Procurement } from '@/types/database.types';
import { useQueryClient } from '@tanstack/react-query';
import type { ItemWarehouseData } from '@/types/database/procurement-item-warehouse';
import type { InspectionData } from '@/components/warehouse/inspection/InspectionFormCard';

export const useWarehouseInspectionActions = (
  procurement: Procurement | undefined,
  itemWarehouseData?: ItemWarehouseData[]
) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const isMultiItem = (procurement?.items?.length || 0) > 1;

  const handleConfirm = async (inspectionData: InspectionData) => {
    if (!procurement || !user) {
      toast({
        title: "Error",
        description: "Missing procurement or user information",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await updateWarehouseStatus({
        procurement,
        inspectionData: {
          receivedQuantityKg: inspectionData.receivedQuantityKg,
          inspectionNotes: inspectionData.inspectionNotes
        },
        itemWarehouseData: isMultiItem ? itemWarehouseData : undefined,
        userId: user.id,
        action: "confirm"
      });
      const prettyStatus =
        result.status
          .replace("pending_finance", "pending finance")
          .replace("pending_warehouse", "pending warehouse")
          .replace("completed", "completed")
          .replace("rejected", "rejected");
      toast({
        title: "Success",
        description: `Procurement moved to status: ${prettyStatus}`,
      });
      await queryClient.invalidateQueries({ queryKey: ['procurements'] });
      navigate('/warehouse');
    } catch (err: any) {
      console.error("Warehouse confirm error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update procurement",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (inspectionData: InspectionData) => {
    if (!procurement || !user || !inspectionData.rejectionReason) {
      toast({
        title: "Error",
        description: "Please provide a rejection reason",
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await updateWarehouseStatus({
        procurement,
        inspectionData: { 
          receivedQuantityKg: inspectionData.receivedQuantityKg,
          inspectionNotes: inspectionData.inspectionNotes,
          rejectionReason: inspectionData.rejectionReason
        },
        itemWarehouseData: isMultiItem ? itemWarehouseData : undefined,
        userId: user.id,
        action: "reject"
      });
      const prettyStatus =
        result.status
          .replace("pending_finance", "pending finance")
          .replace("pending_warehouse", "pending warehouse")
          .replace("completed", "completed")
          .replace("rejected", "rejected");
      toast({
        title: "Success",
        description: `Procurement moved to status: ${prettyStatus}`,
      });
      await queryClient.invalidateQueries({ queryKey: ['procurements'] });
      navigate('/warehouse');
    } catch (err: any) {
      console.error("Warehouse reject error:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to reject procurement",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleConfirm,
    handleReject,
    isSubmitting
  };
};
